<template>
    <div v-if="visible" class="postponement" :class="{ 'postponement--compact': compact }">
        <!-- the latest request of the team for this match: its status -->
        <div v-if="request" class="postponement__status" :class="`postponement__status--${request.status}`">
            <v-icon small>{{ statusIcon }}</v-icon>
            <span>{{ statusLabel }}</span>
        </div>

        <!-- after a rejection or a cancellation the team may ask again -->
        <template v-if="!match.postponed && (!request || request.status === 'rejected' || request.status === 'cancelled') && startTime > now">
            <v-btn
                small
                depressed
                :disabled="!canRequest"
                :loading="loading"
                class="postponement__btn"
                @click="dialog = true"
            >
                <v-icon small left>mdi-calendar-remove-outline</v-icon>
                Αίτημα αναβολής
            </v-btn>
            <div v-if="summary" class="postponement__hint" :class="{ 'postponement__hint--warn': !canRequest }">
                {{ hint }}
            </div>
        </template>

        <v-dialog v-model="dialog" max-width="420">
            <v-card class="postponement-dialog">
                <v-card-title class="postponement-dialog__title">
                    <v-icon color="orange darken-2" class="mr-2">mdi-calendar-remove-outline</v-icon>
                    Αίτημα αναβολής
                </v-card-title>
                <v-card-text>
                    <div class="postponement-dialog__match">{{ teamsLabel }}</div>
                    <div class="postponement-dialog__date">{{ dateLabel }}</div>
                    <p class="mt-3 mb-2">
                        Θα χρησιμοποιηθεί <b>1</b> από τις <b>{{ summary ? summary.remaining : '–' }}</b> διαθέσιμες αναβολές της ομάδας για τη σεζόν.
                        Ο αγώνας αναβάλλεται μόλις εγκριθεί το αίτημα από τη διοργάνωση.
                    </p>
                    <v-textarea
                        v-model="reason"
                        label="Αιτιολογία (προαιρετικό)"
                        color="orange"
                        outlined
                        auto-grow
                        rows="2"
                        counter="500"
                        maxlength="500"
                        hide-details="auto"
                    />
                    <div v-if="error" class="red--text text-body-2 mt-3">{{ error }}</div>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text :disabled="loading" @click="dialog = false">Άκυρο</v-btn>
                    <v-spacer />
                    <v-btn depressed color="orange darken-2" class="white--text" :loading="loading" @click="submit">
                        Αποστολή αιτήματος
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="6000">
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import postponementService from '@/services/postponement'

    const ERROR_MESSAGES = {
        POSTPONEMENT_LIMIT_REACHED: 'Η ομάδα έχει εξαντλήσει τις αναβολές της σεζόν.',
        POSTPONEMENT_ALREADY_REQUESTED: 'Έχει ήδη γίνει αίτημα αναβολής για αυτόν τον αγώνα.',
        POSTPONEMENT_NOT_ALLOWED: 'Ο αγώνας δεν μπορεί πλέον να αναβληθεί.',
        NO_PLAYER_ON_TEAM: 'Ο παίκτης του προφίλ σας δεν ανήκει στην ομάδα. Ενημερώστε το προφίλ σας.',
        PLAYER_NOT_FOUND: 'Ο παίκτης του προφίλ σας δεν βρέθηκε. Ενημερώστε το προφίλ σας.',
        MATCH_NOT_INCLUDES_TEAM: 'Η ομάδα σας δεν συμμετέχει σε αυτόν τον αγώνα.',
        NOT_TEAM_CAPTAIN: 'Μόνο ο αρχηγός της ομάδας μπορεί να ζητήσει αναβολή.'
    }

    const STATUS = {
        pending: { label: 'Το αίτημα αναβολής περιμένει έγκριση', icon: 'mdi-timer-sand' },
        approved: { label: 'Το αίτημα αναβολής εγκρίθηκε', icon: 'mdi-check-circle-outline' },
        rejected: { label: 'Το αίτημα αναβολής απορρίφθηκε', icon: 'mdi-close-circle-outline' },
        cancelled: { label: 'Το αίτημα αναβολής ακυρώθηκε', icon: 'mdi-cancel' }
    }

    // The postponement request of a match, for the captain of one of the two teams (as picked on the profile).
    // `postponements` (the team summary) can be passed by a parent that shows many matches, otherwise it is fetched.
    export default {
        name: 'PostponementRequest',
        props: {
            // needs id, matchDatetime, postponed, team1 and team2
            match: {
                type: Object,
                required: true
            },
            postponements: {
                type: Object,
                default: null
            },
            compact: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                fetched: null,
                dialog: false,
                reason: '',
                loading: false,
                error: null,
                snackbar: false,
                snackbarText: '',
                snackbarColor: 'success',
                now: Date.now()
            }
        },
        computed: {
            team() {
                return this.$store.getters.getTeam
            },
            isCaptainOfMatch() {
                if (!this.$store.getters.getCaptain || !this.team) return false
                return [this.match.team1, this.match.team2].some(team => team && team.id === this.team.id)
            },
            startTime() {
                return this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).valueOf() : null
            },
            visible() {
                if (!this.isCaptainOfMatch) return false
                // a request is shown until the match, a new one only for a scheduled match
                return !!this.request || (!!this.startTime && this.startTime > this.now)
            },
            summary() {
                return this.postponements || this.fetched
            },
            request() {
                // the latest request of the team for the match (rejected ones stay, so the captain sees the answer)
                return ((this.summary && this.summary.requests) || []).find(request => request.matchId === this.match.id) || null
            },
            deadline() {
                if (!this.summary || !this.startTime) return null
                return this.startTime - this.summary.deadlineHours * 60 * 60 * 1000
            },
            deadlinePassed() {
                return this.deadline !== null && this.now >= this.deadline
            },
            canRequest() {
                return !!this.summary && this.summary.remaining > 0 && !this.deadlinePassed
            },
            hint() {
                if (!this.summary) return ''
                if (this.summary.remaining <= 0) {
                    return `Η ομάδα έχει εξαντλήσει τις αναβολές της σεζόν (${this.summary.used}/${this.summary.allowed}).`
                }
                if (this.deadlinePassed) {
                    return `Το αίτημα αναβολής γίνεται έως ${this.summary.deadlineHours} ώρες πριν τον αγώνα.`
                }
                return `Διαθέσιμες αναβολές: ${this.summary.remaining} από ${this.summary.allowed} · έως ${momentTimezone(this.deadline).tz('Europe/Athens').locale('el').format('ddd D/M HH:mm')}`
            },
            statusLabel() {
                return STATUS[this.request.status] ? STATUS[this.request.status].label : ''
            },
            statusIcon() {
                return STATUS[this.request.status] ? STATUS[this.request.status].icon : 'mdi-information-outline'
            },
            teamsLabel() {
                return `${(this.match.team1 && this.match.team1.name) || ''} - ${(this.match.team2 && this.match.team2.name) || ''}`
            },
            dateLabel() {
                return this.startTime ? momentTimezone(this.startTime).tz('Europe/Athens').locale('el').format('dddd D MMMM · HH:mm') : ''
            }
        },
        watch: {
            // the match page loads the match after the component is created
            isCaptainOfMatch(captain) {
                if (captain && !this.postponements && !this.fetched) {
                    this.fetch()
                }
            },
            dialog(open) {
                if (open) {
                    this.error = null
                    this.now = Date.now()
                }
            }
        },
        created() {
            if (!this.postponements && this.isCaptainOfMatch) {
                this.fetch()
            }
        },
        methods: {
            fetch() {
                postponementService.getTeamPostponements(this.team.id).then(resp => {
                    this.fetched = resp.data
                }).catch(error => console.error('Could not load the postponements of the team', error))
            },
            submit() {
                this.loading = true
                this.error = null
                postponementService.requestPostponement({
                    matchId: this.match.id,
                    teamId: this.team.id,
                    playerId: this.$store.getters.getPlayer.id,
                    reason: this.reason
                }).then(resp => {
                    this.dialog = false
                    this.reason = ''
                    this.snackbarText = 'Το αίτημα στάλθηκε. Ο αγώνας θα αναβληθεί μόλις εγκριθεί από τη διοργάνωση.'
                    this.snackbarColor = 'success'
                    this.snackbar = true
                    this.$emit('requested', resp.data)
                    if (!this.postponements) {
                        this.fetch()
                    }
                }).catch(error => {
                    const data = (error.response && error.response.data) || {}
                    if (data.code === 'POSTPONEMENT_DEADLINE_PASSED') {
                        this.error = `Η προθεσμία έληξε: το αίτημα αναβολής γίνεται έως ${this.summary ? this.summary.deadlineHours : ''} ώρες πριν τον αγώνα.`
                    } else {
                        this.error = ERROR_MESSAGES[data.code] || 'Δεν ήταν δυνατή η αποστολή του αιτήματος. Δοκιμάστε ξανά.'
                    }
                    // the limit or an earlier request may have changed meanwhile
                    if (!this.postponements) {
                        this.fetch()
                    } else {
                        this.$emit('requested', null)
                    }
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    }
</script>

<style scoped>
.postponement {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    margin-top: 12px;
}

.postponement--compact {
    margin-top: 0;
    align-items: flex-end;
}

.postponement__btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 700;
    background: #fff3e0 !important;
    color: #e65100 !important;
}

.postponement__hint {
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.6);
}

.postponement--compact .postponement__hint {
    text-align: right;
}

.postponement__hint--warn {
    color: #c62828;
}

.postponement__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 700;
}

.postponement__status .v-icon {
    color: inherit !important;
}

.postponement__status--pending {
    background: #fff3e0;
    color: #e65100;
}

.postponement__status--approved {
    background: #e8f5e9;
    color: #2e7d32;
}

.postponement__status--cancelled {
    background: #eceff1;
    color: #546e7a;
}

.postponement__status--rejected {
    background: #ffebee;
    color: #c62828;
}

.postponement-dialog__title {
    font-weight: 800;
    word-break: normal;
}

.postponement-dialog__match {
    font-weight: 800;
    font-size: 1.05rem;
    color: rgba(0, 0, 0, 0.87);
}

.postponement-dialog__date {
    font-size: 0.85rem;
}
</style>
